import { useState, useEffect } from 'react';
import { Save, Smartphone } from 'lucide-react';
import { systemService } from '../../../services';
import type { DiscoverySettings, AppDiscoverySettings } from '../../../services';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface DiscoverySettingsTabProps {
    discoverySettings: DiscoverySettings | null;
    onDiscoveryUpdate: (settings: DiscoverySettings) => void;
    appDiscoverySettings: AppDiscoverySettings | null;
    onAppDiscoveryUpdate: (settings: AppDiscoverySettings) => void;
}

const DiscoverySettingsTab = ({
    discoverySettings,
    onDiscoveryUpdate,
    appDiscoverySettings,
    onAppDiscoveryUpdate,
}: DiscoverySettingsTabProps) => {
    const toast = useToast();
    const { canUpdate } = usePermissions();
    const [saving, setSaving] = useState(false);
    const [discoveryData, setDiscoveryData] = useState<DiscoverySettings | null>(discoverySettings);
    const [appDiscoveryData, setAppDiscoveryData] = useState<AppDiscoverySettings | null>(appDiscoverySettings);

    useEffect(() => {
        setDiscoveryData(discoverySettings);
    }, [discoverySettings]);

    useEffect(() => {
        setAppDiscoveryData(appDiscoverySettings);
    }, [appDiscoverySettings]);

    const handleSave = async () => {
        if (!discoveryData) return;

        try {
            setSaving(true);

            const promises: Promise<any>[] = [
                systemService.updateDiscovery(discoveryData),
            ];

            if (appDiscoveryData) {
                promises.push(systemService.updateAppDiscovery(appDiscoveryData));
            }

            const results = await Promise.all(promises);
            onDiscoveryUpdate(results[0]);
            if (results[1]) {
                onAppDiscoveryUpdate(results[1]);
            }

            toast.success('Settings Updated', 'Discovery settings updated successfully');
        } catch (err: any) {
            toast.error('Update Failed', `Failed to update discovery settings: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (!discoveryData) {
        return <div className="text-gray-500">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            {/* MQTT Configuration */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">MQTT Configuration</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                        label="MQTT Host"
                        value={discoveryData.mqttHost}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, mqttHost: e.target.value })}
                    />
                    <FormField
                        label="MQTT Port"
                        value={discoveryData.mqttPort.toString()}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, mqttPort: parseInt(e.target.value) || 1883 })}
                        type="number"
                    />
                    <FormField
                        label="MQTT TLS Port"
                        value={discoveryData.mqttTlsPort.toString()}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, mqttTlsPort: parseInt(e.target.value) || 8883 })}
                        type="number"
                    />
                </div>
            </div>

            {/* Orchestrator API Configuration */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Orchestrator API</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Organisation Code</label>
                        <input
                            type="text"
                            value={discoveryData.orchestratorCode || ''}
                            onChange={(e) => setDiscoveryData({ ...discoveryData, orchestratorCode: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '') })}
                            className="input-base w-full font-mono"
                            placeholder="e.g. NET-LNK"
                            maxLength={20}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            The organisation code for VPN app setup. Must match the code set in Global VSA.
                        </p>
                    </div>
                    <FormField
                        label="API Host"
                        value={discoveryData.apiHost}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, apiHost: e.target.value })}
                    />
                    <FormField
                        label="API Port"
                        value={discoveryData.apiPort.toString()}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, apiPort: parseInt(e.target.value) || 4000 })}
                        type="number"
                    />
                    <FormField
                        label="API Protocol"
                        value={discoveryData.apiProtocol}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, apiProtocol: e.target.value })}
                        type="select"
                        options={['http', 'https']}
                    />
                    <FormField
                        label="Discovery TTL (seconds)"
                        value={discoveryData.discoveryTtl.toString()}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, discoveryTtl: parseInt(e.target.value) || 300 })}
                        type="number"
                    />
                </div>
            </div>

            {/* SSH Bastion Configuration */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">SSH Bastion Configuration</h2>
                <p className="text-sm text-gray-500 mb-4">Configuration for edge device reverse SSH tunnels</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                        label="Bastion Host"
                        value={discoveryData.sshBastionHost || ''}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, sshBastionHost: e.target.value })}
                    />
                    <FormField
                        label="Bastion Port"
                        value={(discoveryData.sshBastionPort || 2222).toString()}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, sshBastionPort: parseInt(e.target.value) || 2222 })}
                        type="number"
                    />
                    <FormField
                        label="Bastion User"
                        value={discoveryData.sshBastionUser || 'tunnel'}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, sshBastionUser: e.target.value })}
                    />
                </div>
            </div>

            {/* Observability Configuration */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Observability Configuration</h2>
                <p className="text-sm text-gray-500 mb-4">URLs for edge device logging and metrics</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        label="Loki URL"
                        value={discoveryData.lokiUrl || ''}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, lokiUrl: e.target.value })}
                    />
                    <FormField
                        label="Prometheus Pushgateway URL"
                        value={discoveryData.prometheusPushgatewayUrl || ''}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, prometheusPushgatewayUrl: e.target.value })}
                    />
                </div>
            </div>

            {/* Network Services Configuration */}
            <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Network Services</h2>
                <p className="text-sm text-gray-500 mb-4">DNS, NTP, and private DNS zone configuration for edge devices</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                        label="DNS Servers"
                        value={discoveryData.dnsServers || '1.1.1.1,8.8.8.8'}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, dnsServers: e.target.value })}
                    />
                    <FormField
                        label="NTP Servers"
                        value={discoveryData.ntpServers || 'pool.ntp.org'}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, ntpServers: e.target.value })}
                    />
                    <FormField
                        label="Private DNS Zone Suffix"
                        value={discoveryData.dnsZoneSuffix || 'sl.internal'}
                        onChange={(e) => setDiscoveryData({ ...discoveryData, dnsZoneSuffix: e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '') })}
                    />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                    Tenant FQDNs will be <code className="bg-gray-100 px-1 rounded">{'<hostname>.<slug>.' + (discoveryData.dnsZoneSuffix || 'sl.internal')}</code>
                </p>
            </div>

            {/* VPN App Discovery Configuration */}
            {appDiscoveryData && (
                <div className="border-t pt-6">
                    <div className="flex items-center space-x-2 mb-4">
                        <Smartphone className="w-5 h-5 text-primary-600" />
                        <h2 className="text-lg font-semibold text-gray-900">VPN App Discovery</h2>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                        Configuration returned by the public discovery endpoint (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/api/v2/app/discovery</code>).
                        VPN apps use this to automatically configure authentication and connection settings.
                    </p>

                    {/* Keycloak Configuration */}
                    <div className="mb-6">
                        <h3 className="text-md font-medium text-gray-800 mb-3">Keycloak Authentication</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormField
                                label="Keycloak URL"
                                value={appDiscoveryData.keycloakUrl || ''}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, keycloakUrl: e.target.value })}
                            />
                            <FormField
                                label="Realm"
                                value={appDiscoveryData.keycloakRealm || ''}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, keycloakRealm: e.target.value })}
                            />
                            <FormField
                                label="Client ID"
                                value={appDiscoveryData.keycloakClientId || 'vsm-client'}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, keycloakClientId: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* VPN Configuration */}
                    <div>
                        <h3 className="text-md font-medium text-gray-800 mb-3">VPN Settings</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormField
                                label="Protocol"
                                value={appDiscoveryData.vpnProtocol || 'wireguard'}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, vpnProtocol: e.target.value })}
                                type="select"
                                options={['wireguard']}
                            />
                            <FormField
                                label="VPN Port"
                                value={(appDiscoveryData.vpnPort || 51821).toString()}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, vpnPort: parseInt(e.target.value) || 51821 })}
                                type="number"
                            />
                            <FormField
                                label="Persistent Keepalive (seconds)"
                                value={(appDiscoveryData.persistentKeepalive || 25).toString()}
                                onChange={(e) => setAppDiscoveryData({ ...appDiscoveryData, persistentKeepalive: parseInt(e.target.value) || 25 })}
                                type="number"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Save Button */}
            {canUpdate && (
                <div className="flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

const FormField = ({
    label,
    value,
    onChange,
    type = 'text',
    options
}: {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    type?: 'text' | 'number' | 'select';
    options?: string[];
}) => {
    if (type === 'select' && options) {
        return (
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                <select value={value} onChange={onChange} className="input-base w-full">
                    {options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>
        );
    }

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                className="input-base w-full"
            />
        </div>
    );
};

export default DiscoverySettingsTab;
